import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { AgGridFilterOption, AgGridTableHeaderLocation } from 'core/enums';
import { PricingHelper } from 'pricing/helpers';
import {
  commonColumns,
  getLineCellClass,
  getLineCellValue,
  QuotePartColId,
  QuotePartInlineActionDrawer,
} from 'quotes/components/QuotePartList';
import {
  EditLineCell,
  ExtendedPriceCell,
  NotesCell,
  PartContentCell,
  PnbPricingCell,
  PriceCell,
  QuantityCell,
  UomCell,
} from 'quotes/elements';
import { QuoteEntityType, QuoteType, QuoteVersionType } from 'quotes/enums';
import { QuotePartHelper, VendorQuoteHelper } from 'quotes/helpers';
import { IQuotePart, IVendorQuote, IVendorQuotePart } from 'quotes/types';

interface IProps {
  openInlineActionDrawer: (quotePart: IQuotePart, action: QuotePartInlineActionDrawer) => void;
  hasTaxonomyColumn?: boolean;
  quoteEntityType?: QuoteEntityType;
  costDecimals?: number;
  quote?: IVendorQuote | null;
  hasVendorQuoteEditPermission?: boolean;
}

export const columnDef = ({
  openInlineActionDrawer,
  hasTaxonomyColumn = false,
  quoteEntityType = QuoteEntityType.QUOTE,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
  quote,
  hasVendorQuoteEditPermission = false,
}: IProps): ColDef<any>[] => {
  const quoteParams = { quote, quoteEntityType, hasVendorQuoteEditPermission };

  const isLineEditable = ({ data }) =>
    VendorQuoteHelper.isQuotePartQuantityEditable({ ...quoteParams, quotePart: data });

  const isUomEditable = ({ data }) =>
    VendorQuoteHelper.isQuotePartUomEditable({ ...quoteParams, quotePart: data });

  const isCostEditable = ({ data }) =>
    VendorQuoteHelper.isQuotePartCostEditable({ ...quoteParams, quotePart: data });

  const isEditLineAllowed = ({ data }) =>
    VendorQuoteHelper.isQuotePartEditLineAllowed({ ...quoteParams, quotePart: data });

  const isLineSelectPartAllowed = ({ data }) =>
    VendorQuoteHelper.isQuotePartSelectPartAllowed({ ...quoteParams, quotePart: data });

  // Quote-level half of `isQuotePartEditLineAllowed`. When it is false no row can pass the
  // per-row half either, so the column is dropped outright rather than rendered blank on every
  // line — a blank action column still eats width and offers itself in the Columns tool panel.
  const canEditLine = VendorQuoteHelper.canEditImportedVendorQuote(quoteParams);

  const {
    aliasColumn,
    allInventoryColumn,
    alternateColumn,
    alternateVendorPartNumberColumn,
    customerPartNumberColumn,
    datasheetColumn,
    descriptionColumn,
    imageColumn,
    inventoryStatusColumn,
    lineTypeColumn,
    manufacturerColumn,
    locationInventoryColumn,
    packagingColumn,
    statusColumn,
    taxonomyColumn,
  } = commonColumns(hasTaxonomyColumn, openInlineActionDrawer, () => false);

  return [
    {
      colId: QuotePartColId.LINE,
      headerName: '#',
      pinned: 'left',
      field: 'line',
      wrapText: false,
      suppressPaste: true,
      suppressFillHandle: true,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      resizable: false,
      minWidth: 45,
      width: 45,
      maxWidth: 45,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      lockPosition: true,
      lockPinned: true,
      enableValue: true,
      enablePivot: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: false,
      valueGetter: ({ data }) => getLineCellValue(data),
      headerClass: 'paddingSmall',
      cellClass: ({ data }) => getLineCellClass(data, false),
      cellRendererParams: { placeholder: '' },
    },
    canEditLine
      ? {
          colId: QuotePartColId.EDIT_LINE,
          headerName: t('headerEditLine'),
          headerValueGetter: ({ location }: { location: AgGridTableHeaderLocation }) =>
            AgGridTableHeaderLocation.HEADER === location ? '' : t('headerEditLine'),
          pinned: 'left',
          minWidth: 45,
          width: 45,
          maxWidth: 45,
          resizable: false,
          sortable: false,
          filter: false,
          floatingFilter: false,
          suppressHeaderMenuButton: true,
          suppressPaste: true,
          suppressFillHandle: true,
          suppressMovable: true,
          lockPosition: true,
          lockPinned: true,
          enableRowGroup: false,
          // Action column: no field or valueGetter, so keep it out of CSV/Excel exports.
          context: { nonExportable: true },
          headerClass: 'paddingSmall',
          cellClass: 'paddingSmall',
          cellRenderer: EditLineCell,
          cellRendererParams: {
            onEditPartLineClick: (quotePart: IQuotePart) =>
              openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.EDIT_LINE),
            isRowEditable: isEditLineAllowed,
          },
        }
      : null,
    imageColumn,
    {
      colId: QuotePartColId.PART_NUMBER,
      headerName: t('headerPartNumber'),
      filter: 'agTextColumnFilter',
      field: 'partArr.number',
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 160,
      maxWidth: 400,
      enableValue: true,
      enablePivot: true,
      valueGetter: ({ data }: { data: IVendorQuotePart }) =>
        data && QuotePartHelper.getDisplayedPartNumber(data),
      filterValueGetter: ({ data }: { data: IVendorQuotePart }) =>
        data &&
        [QuotePartHelper.getDisplayedPartNumber(data), data.vendorPartNumber]
          .filter(Boolean)
          .join(' '),
      cellRenderer: PartContentCell,
      cellRendererParams: {
        onPartNumberClick: (quotePart: IQuotePart) =>
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.DETAILS),
        isRowEditable: isLineEditable,
        isRowSelectPartAllowed: isLineSelectPartAllowed,
        quoteVersionType: QuoteVersionType.RELEASE,
        quoteType: QuoteType.VENDOR,
      },
    },
    manufacturerColumn,
    descriptionColumn,
    {
      colId: QuotePartColId.QUANTITY,
      headerName: t('headerQuantity'),
      field: 'quantity',
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sumQty',
      minWidth: 75,
      cellStyle: { alignItems: 'flex-end' },
      editable: isLineEditable,
      cellRenderer: QuantityCell,
    },
    {
      colId: QuotePartColId.UOM,
      headerName: t('headerUom'),
      field: 'uom',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      minWidth: 75,
      suppressPaste: true,
      suppressFillHandle: true,
      editable: isUomEditable,
      cellEditorSelector: ({ data }) => ({
        component: 'agRichSelectCellEditor',
        params: {
          values: data?.allowedUoms,
        },
      }),
      cellRenderer: UomCell,
    },
    {
      colId: QuotePartColId.NOTES,
      headerName: t('headerNotes'),
      filter: 'agTextColumnFilter',
      field: 'defaultNotes',
      valueGetter: ({ data }) => data?.notes,
      sortable: true,
      minWidth: 200,
      width: 200,
      maxWidth: 500,
      cellRenderer: NotesCell,
    },
    {
      colId: QuotePartColId.PRICE,
      headerName: t('headerUnitCost'),
      field: 'price',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      minWidth: 120,
      maxWidth: 180,
      cellStyle: { alignItems: 'flex-end' },
      editable: isCostEditable,
      valueGetter: ({ data, api, node, column }: ValueGetterParams) => {
        const isEditing = api
          .getEditingCells()
          ?.some(
            (cell) =>
              cell.rowIndex === node.rowIndex && cell.column.getColId() === column.getColId()
          );

        if (isEditing) {
          return data?.price;
        }

        return data?.includeFreight ? data?.priceWithFreight : data?.price;
      },
      cellEditorParams: ({ data }) => ({
        value: data?.price,
      }),
      cellRenderer: PriceCell,
      cellRendererParams: {
        treatAsCost: true,
        showPriceGuidance: false,
      },
    },
    {
      colId: QuotePartColId.EXT_PRICE,
      headerName: t('headerExtCost'),
      field: 'extendedPrice',
      width: 80,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      valueGetter: ({ data }) =>
        data?.includeFreight ? data?.extendedPriceWithFreight : data?.extendedPrice,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: ExtendedPriceCell,
      cellRendererParams: {
        treatAsCost: true,
        costDecimals,
      },
    },
    {
      colId: QuotePartColId.BASE,
      headerName: t('headerBase'),
      field: 'priceBase',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        quoteType: QuoteType.VENDOR,
        costDecimals,
      },
    },
    {
      colId: QuotePartColId.ADDER,
      headerName: t('headerAdder'),
      field: 'priceAdder',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        quoteType: QuoteType.VENDOR,
        type: 'adder',
        costDecimals,
      },
    },
    locationInventoryColumn,
    allInventoryColumn,
    inventoryStatusColumn,
    taxonomyColumn,
    packagingColumn,
    alternateColumn,
    alternateVendorPartNumberColumn,
    customerPartNumberColumn,
    aliasColumn,
    statusColumn,
    lineTypeColumn,
    datasheetColumn,
  ].filter((column) => !!column) as ColDef[];
};
